import I18n from 'APP/I18n'
import React, { useCallback, useEffect, useState } from 'react'

import { AutocompleteInput, AutocompleteOption } from 'APP/Converse/Inputs/AutocompleteInput'
import { createSearchIndex } from 'APP/Helpers/createSearchIndex'

export type SpecialtyKey =
  | 'wic'
  | 'er'
  | 'oncologist'
  | 'childPsychiatrist'
  | 'neuropsychologist'
  | 'physiatrist'
  | 'painClinic'
  | 'sleepMedicine'
  | 'colposcopy'
  | 'travelHealth'
  | 'colonoscopy'
  | 'urologist'
  | 'vascularSurgery'
  | 'infectiousDisease'
  | 'sportsPhysician'
  | 'speechTherapist'
  | 'pharmacy'
  | 'rheumatologist'
  | 'geneticTesting'
  | 'physiotherapist'
  | 'allergyTesting'
  | 'abpmTest'
  | 'pregnancyFollowUp'
  | 'podiatrist'
  | 'familyPlanning'
  | 'pediatrician'
  | 'womensHealth'
  | 'otolaryngologist'
  | 'abortion'
  | 'orthotherapist'
  | 'hormoneTherapy'
  | 'orthopedist'
  | 'genderAffirmingCare'
  | 'optometrist'
  | 'vasectomy'
  | 'ophthalmologist'
  | 'minorSurgery'
  | 'obstetrician'
  | 'nexplanonInsertion'
  | 'neurologist'
  | 'iudRemoval'
  | 'nephrologist'
  | 'vaccinationServices'
  | 'midwife'
  | 'tuberculineSkinTest'
  | 'kinesologist'
  | 'iudInsertion'
  | 'internist'
  | 'cryotherapy'
  | 'immunologist'
  | 'cortisoneInjection'
  | 'guidanceCounselor'
  | 'sleepApneaTest'
  | 'hematologist'
  | 'psychoeducator'
  | 'gynecologist'
  | 'stiScreening'
  | 'geneticist'
  | 'papTest'
  | 'lgbtq2sServices'
  | 'gastroenterologist'
  | 'laboratoryServices'
  | 'sexologist'
  | 'occupationalTherapist'
  | 'strepTest'
  | 'remedialEducation'
  | 'bloodTest'
  | 'fertilityClinic'
  | 'vaginalCulture'
  | 'couplesCounselling'
  | 'endocrinologist'
  | 'urineTest'
  | 'couplesTherapy'
  | 'dietitian'
  | 'holterTest'
  | 'familyCounselling'
  | 'dermatologist'
  | 'electrocardiogram'
  | 'familyTherapy'
  | 'dentist'
  | 'ultrasound'
  | 'groupTherapy'
  | 'interventionalRadiology'
  | 'chiropractor'
  | 'communityHealthCentre'
  | 'groupCounselling'
  | 'orthopedicSurgeon'
  | 'radiology'
  | 'psychiatrist'
  | 'clsc'
  | 'cardiologist'
  | 'mri'
  | 'psychotherapist'
  | 'privateClinic'
  | 'audiologist'
  | 'xray'
  | 'psychologist'
  | 'familyDoctor'
  | 'allergist'
  | 'ctScan'
  | 'socialWorker'
  | 'spirometry'
  | 'hearingTest'
  | 'petScan'
  | 'colorectalCancerScreening'
  | 'sexualHealthClinic'
  | 'rehabilitationCenter'
  | 'woundCare'
  | 'earCleaning'
  | 'youthClinics'
  | 'prep'
  | 'pep'
  | 'hivScreening'
  | 'hivAidsServices'
  | 'addictionSupport'
  | 'addictionCounsellor'
  | 'smokingCessationSupport'
  | 'crisisIntervention'
  | 'addictionTreatmentCenters'
  | 'griefCounselling'
  | 'bereavementCounselling'
  | 'infusionTherapy'
  | 'partnerLabsBloodUrineTests'

export type SpecialtyValue = { label: string; q: string }

type SpecialtiesMap = Record<SpecialtyKey, SpecialtyValue>

export interface SpecialtyPickerProps {
  initialSpecialtyKey?: SpecialtyKey
  onValueChange: (value: SpecialtyValue) => void
}

// Constant Source Data - no need for rerender
const specialtiesMap = I18n.t('SelfNavigationScreen.specialties', {
  returnObjects: true,
}) as SpecialtiesMap

const specialtiesList = Object.entries(specialtiesMap).map(([key, value]) => ({
  value: key,
  label: value.label,
}))

const specialtiesIndex = createSearchIndex(specialtiesList, ['label'])

export function loadSpecialtyOptions(inputValue: string): AutocompleteOption[] {
  return !inputValue ? specialtiesList : specialtiesIndex.search(inputValue).map((r) => r.item)
}

function SpecialtyPicker({ initialSpecialtyKey, onValueChange }: SpecialtyPickerProps) {
  const [option, setOption] = useState<AutocompleteOption | null>(
    initialSpecialtyKey && specialtiesMap[initialSpecialtyKey]
      ? { value: initialSpecialtyKey, ...specialtiesMap[initialSpecialtyKey] }
      : null
  )

  const loadOptions = useCallback(loadSpecialtyOptions, [])

  useEffect(() => {
    if (option) {
      onValueChange(specialtiesMap[option.value as SpecialtyKey])
    }
  }, [option, onValueChange])

  return (
    <AutocompleteInput
      label={I18n.t('SelfNavigationScreen.specialtyLabel')}
      showRequiredAsterisk
      option={option}
      loadOptions={loadOptions}
      onChange={(option) => {
        setOption(option)
      }}
      infotip={I18n.t('SelfNavigationScreen.specialtyInfotip')}
      isPersistentInfotip
      testID="SPECIALTY_PICKER"
    />
  )
}

export default SpecialtyPicker
